/**
 * admin/src/hooks/useStudents.ts
 * Hooks cho Students page
 *
 * useStudents   — danh sách có server-side filter/paginate/sort
 * useStudent    — 1 học sinh detail (profile + skills + attempts + heatmap)
 */

import { useState, useEffect, useCallback } from 'react';
import { adminFetch } from './useAdmin';
import type {
  AdminStudentProfile,
  AdminStudentDetail,
  PaginatedResponse,
  SubscriptionTier,
  UpdateStudentPayload,
  UpdateParentPayload,
} from '../types/admin';


// ─── Params ───────────────────────────────────────────────────────────────────
export interface StudentsParams {
  page?: number;
  limit?: number;
  search?: string;
  tier?: SubscriptionTier | '';  // filter theo tier của phụ huynh
  sort?: string;
  dir?: 'asc' | 'desc';
}

interface UseStudentsReturn {
  students: AdminStudentProfile[];
  total: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useStudents(params: StudentsParams = {}): UseStudentsReturn {
  const [data, setData] = useState<PaginatedResponse<AdminStudentProfile> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    adminFetch<PaginatedResponse<AdminStudentProfile>>('/students', {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        search: params.search || undefined,
        tier: params.tier || undefined,
        sort: params.sort || 'total_stars',
        dir: params.dir || 'desc',
      },
    })
      .then((res) => { if (!cancelled) { setData(res); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi tải students');
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), tick]);

  return {
    students: data?.data ?? [],
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 1,
    isLoading,
    error,
    refetch,
  };
}

// ─── Student Detail ───────────────────────────────────────────────────────────
interface UseStudentReturn {
  student: AdminStudentDetail | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useStudent(studentId: string | undefined): UseStudentReturn {
  const [student, setStudent] = useState<AdminStudentDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!studentId) { setIsLoading(false); return; }
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    adminFetch<AdminStudentDetail>(`/students/${studentId}`)
      .then((res) => { if (!cancelled) { setStudent(res); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi tải student');
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [studentId, tick]);

  return { student, isLoading, error, refetch };
}

// ─── Mutations (plain async functions — dùng trong event handlers) ────────────

/**
 * Cập nhật thông tin học sinh (display_name, avatar_id, theme)
 * PATCH /api/admin/students/:id
 */
export async function updateStudent(
  studentId: string,
  payload: UpdateStudentPayload
): Promise<{ ok: boolean; student?: AdminStudentProfile }> {
  return adminFetch<{ ok: boolean; student?: AdminStudentProfile }>(
    `/students/${studentId}`,
    { method: 'PATCH', body: JSON.stringify(payload) }
  );
}

/**
 * Cập nhật thông tin phụ huynh (name, notification_email)
 * PATCH /api/admin/students/:id/parent
 * ⚠️ Không cho sửa email — email là identity gắn với Google OAuth
 */
export async function updateStudentParent(
  studentId: string,
  payload: UpdateParentPayload
): Promise<{ ok: boolean }> {
  return adminFetch<{ ok: boolean }>(
    `/students/${studentId}/parent`,
    { method: 'PATCH', body: JSON.stringify(payload) }
  );
}
